import { useEffect, useState } from "react";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { getMembershipStatus } from "@/lib/checkout";

const LOGO =
  "https://customer-assets.emergentagent.com/job_data-driven-baseball/artifacts/94x88gnl_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2003_59_37%20PM.png";

export default function MembershipSuccess() {
  const [state, setState] = useState("checking"); // checking | active | pending | error
  const [info, setInfo] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await getMembershipStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid" && data.membership_status === "active") {
          setState("active");
          return;
        }
      } catch (err) {
        console.error("Membership status poll failed:", err);
      }
      if (attempts >= 8) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        <img src={LOGO} alt="Rep Lab" className="h-20 w-auto object-contain mix-blend-screen mx-auto mb-10" />

        {state === "checking" && (
          <div data-testid="membership-success-checking">
            <Loader2 size={40} className="animate-spin text-[#2563EB] mx-auto" />
            <p className="mt-6 text-lg font-semibold text-white/70">Confirming your membership…</p>
          </div>
        )}

        {state === "active" && (
          <div data-testid="membership-success-active">
            <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2563EB]/15 text-[#2563EB]">
              <Check size={32} strokeWidth={3} />
            </div>
            <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase text-white leading-[0.95]">
              Welcome to Rep Lab.
            </h1>
            <p className="mt-6 text-lg font-medium text-white/60 leading-relaxed">
              {info.player_name ? `${info.player_name}'s ` : "Your "}membership is active
              {info.division_name ? ` in the ${info.division_name} division` : ""}. Your reserved weekly roster spot is secured.
            </p>
            <p className="mt-3 text-base font-medium text-white/40">Check your email for confirmation and first-session details.</p>
            <a
              href="/"
              data-testid="success-home-btn"
              className="mt-10 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-base font-bold px-8 py-4 rounded-full transition-colors"
            >
              Back to Home <ArrowRight size={18} />
            </a>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="membership-success-pending">
            <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-white">
              <Check size={32} strokeWidth={3} />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Payment received.</h1>
            <p className="mt-6 text-lg font-medium text-white/60 leading-relaxed">
              We're finalizing your membership. You'll receive a confirmation email shortly.
            </p>
            <a href="/" className="mt-10 inline-flex items-center gap-2 text-[#2563EB] font-bold">
              Back to Home <ArrowRight size={18} />
            </a>
          </div>
        )}

        {state === "error" && (
          <div data-testid="membership-success-error">
            <h1 className="text-3xl font-extrabold tracking-tight text-white">Something went wrong.</h1>
            <p className="mt-6 text-lg font-medium text-white/60">We couldn't confirm your session. If you were charged, email info@replabbaseball.com.</p>
            <a href="/" className="mt-10 inline-flex items-center gap-2 text-[#2563EB] font-bold">
              Back to Home <ArrowRight size={18} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
